// Read an injected Vite env var safely: trim, drop trailing slash, treat
// empty-string as unset (??-only would let "" through and silently ship a
// broken URL — see the MINTER_ARN incident).
function envOr(value: unknown, fallback: string): string {
  const v = typeof value === "string" ? value.trim().replace(/\/$/, "") : "";
  return v || fallback;
}

// Single source of truth for the API base URL.
// Set VITE_API_URL in .env (see .env.example). Falls back to a local Worker.
export const API_URL: string = envOr(import.meta.env.VITE_API_URL, "http://localhost:8787");

// PUBLIC base for URLs handed to EXTERNAL services that must reach our API from
// the internet (e.g. the AWS CloudFormation auto-register callback). In prod this
// equals API_URL (the deployed host); in local dev set VITE_PUBLIC_API_URL to a
// tunnel so the customer's stack Lambda can POST back to us. Defaults to API_URL.
export const PUBLIC_API_URL: string = envOr(import.meta.env.VITE_PUBLIC_API_URL, API_URL);

// OpsBelt admin dashboard — billing + seat/access management live there, not in
// BlobShip. Billing CTAs and "request access" deep-link here (prod: app.opsbelt.com).
export const OPSBELT_APP_URL: string = envOr(
  import.meta.env.VITE_OPSBELT_APP_URL,
  "http://localhost:5174",
);

// "Deploy to Azure" button — grants BlobShip scoped, revocable access to one
// storage account. Both halves (the ARM template and the portal createUi
// definition) are served by our own API: the template is static, and the UI def
// bakes the connected tenancy's BlobShip principal id into a hidden field so the
// customer never pastes it. Override the bases per environment if needed.
const AZURE_TEMPLATE_URI: string = envOr(
  import.meta.env.VITE_AZURE_TEMPLATE_URI,
  `${API_URL}/azure/template`,
);
const AZURE_UI_URL: string = envOr(
  import.meta.env.VITE_AZURE_UI_URL,
  `${API_URL}/azure/ui`,
);

/**
 * Portal "Deploy to Azure" deep-link. The createUIDefinitionUri carries the
 * connected tenancy's BlobShip principal id (`?sp=…`), which the API bakes into a
 * hidden field — so the customer only picks a storage account, never the
 * principal. The grant is meaningless against any other principal, so it's
 * required, not optional.
 */
export function azureDeployUrl(opts: { principalId: string }): string {
  const uiUri = `${AZURE_UI_URL}?sp=${encodeURIComponent(opts.principalId)}`;
  return (
    "https://portal.azure.com/#create/Microsoft.Template/uri/" +
    `${encodeURIComponent(AZURE_TEMPLATE_URI)}/createUIDefinitionUri/${encodeURIComponent(uiUri)}`
  );
}

// "Launch CloudFormation stack" button — the AWS mirror of the Azure deploy
// link. The template is served by our own API (static CloudFormation YAML); the
// per-connector ExternalId + Nonce and the minter principal ride in the quick-
// create URL the console reads. Override the bases per environment if needed.
// CloudFormation's templateURL only accepts S3 URLs (not arbitrary HTTPS); the
// API endpoint mirror is for direct downloads. Set VITE_AWS_TEMPLATE_URL to the
// public S3 URL of connector.yaml.
const AWS_TEMPLATE_URL: string = envOr(
  import.meta.env.VITE_AWS_TEMPLATE_URL,
  `${API_URL}/aws/template`,
);
// The BlobShip minter principal ARN customers must trust — public, not secret.
// Matches the API's AWS_MINTER_PRINCIPAL_ARN; surfaced to the SPA so the URL can
// prefill MinterPrincipalArn without an extra round-trip. Empty-string protection
// matters: an UNSET GitHub Actions `vars.X` injects "" (not null/undefined), and
// an empty MinterPrincipalArn makes CloudFormation produce `"AWS": ""` in the
// trust policy → "Invalid principal in policy".
export const AWS_MINTER_ARN: string = envOr(
  import.meta.env.VITE_AWS_MINTER_ARN,
  "arn:aws:iam::846328242702:user/blobship/blobship-minter",
);

/**
 * CloudFormation quick-create deep-link — the AWS "Launch stack" equivalent of
 * azureDeployUrl. The console fetches our templateURL, then prefills the stack
 * with the per-connector ExternalId + registration Nonce, the minter principal,
 * the callback URL the auto-register Lambda POSTs back to, and (when known) the
 * intake bucket. The customer only confirms and deploys.
 */
export function awsDeployUrl(opts: {
  region: string;
  externalId: string;
  nonce: string;
  callbackUrl: string;
  minterArn?: string;
  bucket?: string;
}): string {
  // Unique stack name per connector (a company installs many) — derived from the
  // per-connector nonce so two installs never clash. Valid CFN stack name chars.
  const suffix = opts.nonce.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12) || "x";
  const params: Record<string, string> = {
    templateURL: AWS_TEMPLATE_URL,
    stackName: `blobship-connector-${suffix}`,
    param_ExternalId: opts.externalId,
    param_MinterPrincipalArn: opts.minterArn ?? AWS_MINTER_ARN,
    param_CallbackUrl: opts.callbackUrl,
    param_Nonce: opts.nonce,
  };
  if (opts.bucket) params.param_IntakeBucketName = opts.bucket;

  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return (
    `https://console.aws.amazon.com/cloudformation/home?region=${encodeURIComponent(opts.region)}` +
    `#/stacks/quickcreate?${qs}`
  );
}
